import { useState } from "react";
import axios from "axios";
import { Check, Copy, Loader2, MessageCircle } from "lucide-react";
import { BRAND, OFFERS, OFFER_NOTE } from "../data/mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const track = (name, label) => window.gtag?.("event", name, { event_category: "engagement", event_label: label });
const inputCls = "w-full rounded-xl border border-[#3915AA]/15 bg-[#F7F5FF] px-3.5 py-2.5 text-sm text-[#1E0A5C] placeholder-[#1E0A5C]/40 outline-none focus:border-[#FC5106] focus:ring-1 focus:ring-[#FC5106]";

function ClaimSuccess({ claim }) {
  const [copied, setCopied] = useState(false);
  const wa = `https://wa.me/${BRAND.whatsapp}?text=${encodeURIComponent(
    `Hi BarknFurs! I'm ${claim.name}. I'd like to book grooming with my offer code ${claim.code} (${claim.offer_title}).`
  )}`;
  const copy = () => { navigator.clipboard?.writeText(claim.code); setCopied(true); };
  return (
    <div data-testid="offer-claim-success" className="text-center">
      <p className="text-xs font-bold uppercase tracking-wider text-[#FC5106]">Your offer code</p>
      <button onClick={copy} data-testid="offer-code-copy" className="mt-2 inline-flex items-center gap-2 rounded-2xl border-2 border-dashed border-[#FC5106]/50 bg-[#FC5106]/5 px-5 py-3 font-mono text-2xl font-bold tracking-widest text-[#3915AA]">
        <span data-testid="offer-code-value">{claim.code}</span>
        {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4 text-[#3915AA]/50" />}
      </button>
      <p className="mt-3 text-sm text-[#1E0A5C]/70">{claim.offer_title}</p>
      {claim.expires_at && (
        <p data-testid="offer-code-expiry" className="mt-1 text-xs font-semibold text-[#FC5106]">
          Valid until {new Date(claim.expires_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })} — book soon!
        </p>
      )}
      <a href={wa} target="_blank" rel="noreferrer" onClick={() => track("generate_lead", "offer_code_whatsapp")} data-testid="offer-claim-whatsapp" className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#FC5106] px-5 py-3 text-sm font-semibold text-white hover:bg-[#E24804] transition-colors">
        <MessageCircle className="h-4 w-4" /> Book on WhatsApp with code
      </a>
      <p className="mt-3 text-[11px] text-[#1E0A5C]/50">Show this code when you book. {OFFER_NOTE}</p>
    </div>
  );
}

export default function OfferClaim({ defaultOffer = OFFERS[0].id, source = "website" }) {
  const [offerId, setOfferId] = useState(defaultOffer);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [claim, setClaim] = useState(null);

  if (claim) return <ClaimSuccess claim={claim} />;

  const submit = async (e) => {
    e.preventDefault();
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 10) return setError("Please enter a valid 10-digit phone number.");
    setBusy(true); setError("");
    try {
      const { data } = await axios.post(`${API}/offers/claim`, { name, phone: phone.trim(), offer_id: offerId });
      setClaim(data);
      track("generate_lead", `offer_claim_${source}`);
    } catch {
      setError("Couldn't create your code. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="offer-claim-form" className="space-y-3 text-left">
      <div className="space-y-2" role="radiogroup">
        {OFFERS.map((o) => (
          <button type="button" key={o.id} role="radio" aria-checked={offerId === o.id} onClick={() => setOfferId(o.id)} data-testid={`offer-option-${o.id}`}
            className={`flex w-full items-start gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors ${offerId === o.id ? "border-[#FC5106] bg-[#FC5106]/5" : "border-[#3915AA]/12 hover:border-[#3915AA]/30"}`}>
            <span className={`mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border-2 ${offerId === o.id ? "border-[#FC5106]" : "border-[#3915AA]/30"}`}>
              {offerId === o.id && <span className="h-2 w-2 rounded-full bg-[#FC5106]" />}
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-[#3915AA]">{o.title} <span className="text-[#FC5106]">· {o.tag}</span></span>
              <span className="block text-xs text-[#1E0A5C]/60">{o.detail}</span>
            </span>
          </button>
        ))}
      </div>
      <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" data-testid="offer-claim-name" className={inputCls} />
      <input required value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone number" inputMode="tel" data-testid="offer-claim-phone" className={inputCls} />
      {error && <p data-testid="offer-claim-error" className="text-xs text-red-600">{error}</p>}
      <button type="submit" disabled={busy} data-testid="offer-claim-submit" className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#FC5106] px-5 py-3 text-sm font-semibold text-white hover:bg-[#E24804] disabled:opacity-60 transition-colors">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Get my offer code
      </button>
      <p className="text-center text-[11px] text-[#1E0A5C]/50">{OFFER_NOTE}</p>
    </form>
  );
}
